import { type FC } from 'react';
import cx from 'classnames';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { UsageReportWeeklyTrend } from 'src/graphql/reports/queryUsageReportDashboard';

import { ChartTooltip } from '../ChartTooltip';

import { useStyles } from './useStyles';

interface AdoptionChartProps {
  className?: string;
  data: UsageReportWeeklyTrend[];
}

const formatAdoptionValue = (value: number, name: string) => {
  if (name === 'Adoption Rate') {
    return `${(value * 100).toFixed(0)}%`;
  }
  return String(value);
};

export const AdoptionChart: FC<AdoptionChartProps> = ({ className, data }) => {
  const styles = useStyles();

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.title}>Adoption & Workspaces</div>
      <ResponsiveContainer
        width="100%"
        height="90%"
      >
        <ComposedChart
          data={data}
          margin={{ top: 10, right: 15, left: 0, bottom: 5 }}
        >
          <defs>
            <linearGradient
              id="adoptionGradient"
              x1="0"
              y1="1"
              x2="0"
              y2="0"
            >
              <stop
                offset="0%"
                stopColor="#7C4DFF"
                stopOpacity={0}
              />
              <stop
                offset="100%"
                stopColor="#7C4DFF"
                stopOpacity={0.3}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            vertical={false}
            strokeDasharray="3 3"
          />
          <XAxis
            dataKey="week"
            tickLine={false}
            tickFormatter={(v) => {
              if (!v) return '';
              const d = new Date(v);
              return `${d.getMonth() + 1}/${d.getDate()}`;
            }}
          />
          <YAxis
            yAxisId="left"
            axisLine={false}
            tickLine={false}
            tickCount={5}
            width={40}
            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            domain={[0, 1]}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            axisLine={false}
            tickLine={false}
            tickCount={5}
            width={40}
          />
          <Tooltip content={<ChartTooltip formatValue={formatAdoptionValue} />} />
          <Legend
            align="left"
            verticalAlign="bottom"
            iconType="line"
          />
          <Area
            yAxisId="left"
            name="Adoption Rate"
            type="monotone"
            dataKey="adoptionRatio"
            stroke="#7C4DFF"
            strokeWidth={2}
            fill="url(#adoptionGradient)"
            dot={{ r: 3, fill: 'white', strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            name="Active Workspaces"
            type="monotone"
            dataKey="activeWorkspacesCount"
            stroke="#FF9800"
            strokeWidth={2}
            dot={{ r: 3, fill: 'white', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
